import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Archive, ArchiveRestore, Ban, Building2, Briefcase, CalendarPlus, Check, Forward, Mail, Paperclip, Send, StickyNote, Trash2, User, UserPlus, X,
} from 'lucide-react';
import { crmConversationsApi, crmContactsApi, crmOrgsApi, crmPipelineApi } from '../../api/crm';
import { companiesApi } from '../../api/companies';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useSocket } from '../../contexts/SocketContext';
import { useConfirm } from '../../components/ConfirmDialog';
import PageHeader from '../../components/PageHeader';
import Modal from '../../components/Modal';
import MessageContent from '../../components/MessageContent';
import CrmActivityForm from '../../components/crm/CrmActivityForm';
import Field from '../../components/crm/Field';
import AiBrief from '../../components/crm/AiBrief';
import { invalidateCrm } from '../../lib/queryCache';
import { SOURCE_LABEL, STATUS_COLOR, STATUS_LABEL, formatHours, formatMoney, joinAddresses, relativeTime, senderLabel, waitingLabel } from '../../lib/crmInbox';
import type { CrmConversation, CrmMessage } from '../../types';

const STAFF_ROLES = new Set(['owner', 'admin', 'technician']);

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function Avatar({ name }: { name: string }) {
  return (
    <span className="w-8 h-8 rounded-full bg-primary-muted flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">
      {(name || '?').trim()[0]?.toUpperCase()}
    </span>
  );
}

/** One message in the thread. Inbound is plain, outbound is tinted with the
 *  brand, notes are tinted warning so nobody mistakes one for an email. */
function MessageCard({ m }: { m: CrmMessage }) {
  const who = m.direction === 'inbound'
    ? (m.fromName || m.fromEmail || 'Them')
    : (m.author?.name || m.fromName || 'Staff');
  const style =
    m.direction === 'outbound' ? { backgroundColor: 'color-mix(in srgb, var(--scpa-primary) 7%, var(--scpa-surface))' }
    : m.direction === 'note' ? { backgroundColor: 'color-mix(in srgb, var(--scpa-status-warning) 10%, var(--scpa-surface))' }
    : undefined;
  return (
    <div className="card" style={style}>
      <div className="flex items-start gap-3">
        <Avatar name={who} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="font-semibold text-fg">{who}</span>
            {m.direction === 'inbound' && m.fromName && <span className="text-xs text-muted">{m.fromEmail}</span>}
            {m.direction === 'outbound' && <span className="text-xs text-muted inline-flex items-center gap-1"><Send className="w-3 h-3" /> to {joinAddresses(m.toEmails)}{m.ccEmails?.length ? ` · cc ${joinAddresses(m.ccEmails)}` : ''}</span>}
            {m.direction === 'note' && <span className="text-xs font-bold text-warn inline-flex items-center gap-1"><StickyNote className="w-3 h-3" /> Internal note — not emailed</span>}
            <span className="ml-auto text-xs text-muted" title={new Date(m.createdAt).toLocaleString()}>{relativeTime(m.createdAt)}</span>
          </div>
          <MessageContent content={m.body} className="mt-2" />
          {m.attachments?.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2">
              {m.attachments.map(a => (
                <li key={a.url}>
                  <a href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-line bg-surface hover:bg-row-odd text-fg">
                    <Paperclip className="w-3 h-3 text-muted" /> {a.filename} <span className="text-muted">{fmtBytes(a.size)}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SideCard({ title, icon, children, action }: { title: string; icon: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="card space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="section-title flex items-center gap-1.5">{icon} {title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function ConversationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const { socket } = useSocket();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const myId = (user as any)?.id ?? (user as any)?._id;

  const { data, isLoading } = useQuery({
    queryKey: ['crm-conversation', id, cid],
    queryFn: () => crmConversationsApi.get(id!),
    enabled: !!id && !!cid,
  });
  const conversation = data?.conversation;
  const messages = data?.messages ?? [];

  const { data: members } = useQuery({
    queryKey: ['company-members', cid],
    queryFn: () => companiesApi.getMembers(cid),
    enabled: !!cid,
  });
  const staff = useMemo(
    () => ((members as any[]) || []).filter(m => STAFF_ROLES.has(m.role) && m.isActive !== false && (m.user?.id || m.userId)),
    [members],
  );

  useEffect(() => {
    if (!socket || !id) return;
    const onEvent = (p: any) => {
      if (p?.conversationId === id) queryClient.invalidateQueries({ queryKey: ['crm-conversation', id] });
    };
    socket.on('crm:conversation', onEvent);
    return () => { socket.off('crm:conversation', onEvent); };
  }, [socket, id, queryClient]);

  // ── Composer ──
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const [body, setBody] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [editRecipients, setEditRecipients] = useState(false);
  useEffect(() => {
    if (conversation) { setTo(conversation.fromEmail); setCc(''); }
  }, [conversation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['crm-conversation', id] });
    queryClient.invalidateQueries({ queryKey: ['crm-conversations'] });
    queryClient.invalidateQueries({ queryKey: ['crm-stats'] });
  };

  const replyMutation = useMutation({
    mutationFn: (closeAfter: boolean) => crmConversationsApi.reply(id!, { body, to, cc: cc || undefined, closeAfter }),
    onSuccess: (_m, closeAfter) => { setBody(''); refresh(); toast.success(closeAfter ? 'Reply sent and conversation closed' : 'Reply sent'); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not send'),
  });
  const noteMutation = useMutation({
    mutationFn: () => crmConversationsApi.note(id!, body),
    onSuccess: () => { setBody(''); refresh(); toast.success('Note added'); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not add note'),
  });
  const updateMutation = useMutation({
    mutationFn: (patch: Parameters<typeof crmConversationsApi.update>[1]) => crmConversationsApi.update(id!, patch),
    onSuccess: () => { refresh(); invalidateCrm(queryClient); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not update'),
  });
  const deleteMutation = useMutation({
    mutationFn: () => crmConversationsApi.delete(id!),
    onSuccess: () => { invalidateCrm(queryClient); navigate('/crm/inbox'); },
  });

  // ── Forward ──
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardTo, setForwardTo] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const forwardMutation = useMutation({
    mutationFn: () => crmConversationsApi.forward(id!, { to: forwardTo, note: forwardNote || undefined }),
    onSuccess: () => { setForwardOpen(false); setForwardTo(''); setForwardNote(''); refresh(); toast.success('Forwarded'); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not forward'),
  });

  // ── Links: contact / organization / deal ──
  const [linkContactOpen, setLinkContactOpen] = useState(false);
  const [linkOrgOpen, setLinkOrgOpen] = useState(false);
  const [dealOpen, setDealOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [dealForm, setDealForm] = useState({ title: '', stageId: '', value: '' });
  const { data: contactsPick } = useQuery({ queryKey: ['crm-contacts-pick', cid], queryFn: () => crmContactsApi.list({ limit: 200 }), enabled: !!cid && linkContactOpen });
  const { data: orgsPick } = useQuery({ queryKey: ['crm-orgs-pick', cid], queryFn: () => crmOrgsApi.list({ limit: 200 }), enabled: !!cid && linkOrgOpen });
  const { data: stages } = useQuery({ queryKey: ['crm-pipeline', cid], queryFn: () => crmPipelineApi.list(), enabled: !!cid && dealOpen });
  useEffect(() => {
    if (dealOpen && conversation) {
      setDealForm({ title: conversation.subject, stageId: stages?.find(s => s.isDefault)?.id ?? stages?.[0]?.id ?? '', value: '' });
    }
  }, [dealOpen, conversation?.id, stages]); // eslint-disable-line react-hooks/exhaustive-deps

  const createContactMutation = useMutation({
    mutationFn: () => crmConversationsApi.createContact(id!),
    onSuccess: () => { refresh(); invalidateCrm(queryClient); toast.success('Contact created'); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not create contact'),
  });
  const createOrgMutation = useMutation({
    mutationFn: () => crmConversationsApi.createOrganization(id!, { name: orgName || undefined }),
    onSuccess: () => { setLinkOrgOpen(false); refresh(); invalidateCrm(queryClient); toast.success('Organization linked'); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not create organization'),
  });
  const createDealMutation = useMutation({
    mutationFn: () => crmConversationsApi.createDeal(id!, { title: dealForm.title, stageId: dealForm.stageId || undefined, value: dealForm.value ? Number(dealForm.value) : null }),
    onSuccess: () => { setDealOpen(false); refresh(); invalidateCrm(queryClient); toast.success('Deal created'); },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not create deal'),
  });

  // ── Tags ──
  const [tagInput, setTagInput] = useState('');
  const [showActivityForm, setShowActivityForm] = useState(false);

  if (isLoading) return <div className="text-muted text-sm">Loading…</div>;
  if (!conversation) return <div className="text-muted text-sm">Conversation not found.</div>;
  const c: CrmConversation = conversation;
  const waiting = waitingLabel(c);
  const canSubmit = body.trim().length > 0 && !replyMutation.isPending && !noteMutation.isPending;
  const submit = (closeAfter = false) => {
    if (!canSubmit) return;
    if (mode === 'note') noteMutation.mutate(); else replyMutation.mutate(closeAfter);
  };
  const setStatus = (status: CrmConversation['status'], extra: { blockSender?: boolean } = {}) => updateMutation.mutate({ status, ...extra });

  const markSpam = async () => {
    const ok = await confirm({ title: 'Mark as spam', message: `Move CRM-${c.number} to Spam? You can reopen it later.`, confirmLabel: 'Mark spam', danger: true });
    if (!ok) return;
    const block = await confirm({ title: 'Block this sender?', message: <>Also drop all future mail from <b>{c.fromEmail}</b> to this mailbox?</>, confirmLabel: 'Block sender', cancelLabel: 'Just mark spam', danger: true });
    setStatus('spam', { blockSender: block });
  };
  const handleDelete = async () => {
    if (await confirm({ title: 'Delete conversation', message: 'Delete this conversation and every message in it? This cannot be undone.', confirmLabel: 'Delete', danger: true })) deleteMutation.mutate();
  };
  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    updateMutation.mutate({ tags: [...(c.tags || []), t] });
    setTagInput('');
  };
  const formEntries = c.formData ? Object.entries(c.formData).filter(([, v]) => v !== null && v !== '' && v !== undefined) : [];

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title={
          <span className="flex flex-col gap-1">
            <span><span className="font-mono text-sm text-muted mr-2">CRM-{c.number}</span>{c.subject}</span>
            <span className="flex items-center gap-3 text-[13px] font-normal flex-wrap">
              <span style={{ fontWeight: 700, color: STATUS_COLOR[c.status] }}>{STATUS_LABEL[c.status]}</span>
              {waiting && <span className={`font-semibold ${waiting.tone === 'err' ? 'text-err' : waiting.tone === 'warn' ? 'text-warn' : 'text-muted'}`}>{waiting.text}</span>}
              <span className="text-muted">{SOURCE_LABEL[c.source]} · with <b className="text-fg">{senderLabel(c)}</b>{c.fromName ? ` <${c.fromEmail}>` : ''}{c.mailbox ? ` · via ${c.mailbox.name}` : ''}</span>
            </span>
          </span>
        }
        actions={
          <>
            <button type="button" className="btn btn-secondary inline-flex items-center gap-1" onClick={() => setForwardOpen(true)} title="Share the whole thread with someone by email">
              <Forward className="w-3.5 h-3.5" /> Forward
            </button>
            {c.status === 'closed' || c.status === 'spam' ? (
              <button type="button" className="btn btn-secondary inline-flex items-center gap-1" onClick={() => setStatus('open')}><ArchiveRestore className="w-3.5 h-3.5" /> Reopen</button>
            ) : (
              <button type="button" className="btn btn-secondary inline-flex items-center gap-1" onClick={() => setStatus('closed')} title="Done for now. A new message from them reopens it."><Archive className="w-3.5 h-3.5" /> Close</button>
            )}
            {c.status !== 'spam' && (
              <button type="button" className="btn btn-secondary inline-flex items-center gap-1" onClick={markSpam}><Ban className="w-3.5 h-3.5" /> Spam</button>
            )}
            <button type="button" className="btn btn-danger" onClick={handleDelete} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Thread ── */}
        <div className="lg:col-span-2 space-y-4">
          {formEntries.length > 0 && (
            <div className="card">
              <h3 className="section-title mb-2">Form submission</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {formEntries.map(([k, v]) => (
                  <div key={k} className="flex gap-2 min-w-0">
                    <dt className="text-muted whitespace-nowrap">{k}:</dt>
                    <dd className="text-fg truncate" title={String(v)}>{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {messages.map(m => <MessageCard key={m.id} m={m} />)}

          {/* Composer */}
          <div className="card space-y-3" id="composer">
            <div className="flex items-center gap-2">
              {(['reply', 'note'] as const).map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setMode(k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${mode === k ? 'bg-primary text-white border-primary' : 'bg-surface text-muted border-line hover:text-fg'}`}
                  title={k === 'reply' ? 'Email them. Their answer comes back to this thread.' : 'A note only your team sees. Never emailed.'}
                >
                  {k === 'reply' ? <><Mail className="w-3.5 h-3.5" /> Reply by email</> : <><StickyNote className="w-3.5 h-3.5" /> Internal note</>}
                </button>
              ))}
              {mode === 'reply' && (
                <button type="button" className="ml-auto text-xs text-primary hover:underline" onClick={() => setEditRecipients(v => !v)}>
                  {editRecipients ? 'Hide recipients' : `To: ${to}${cc ? ` · Cc: ${cc}` : ''}`}
                </button>
              )}
            </div>
            {mode === 'reply' && editRecipients && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="To" required><input className="input w-full" value={to} onChange={e => setTo(e.target.value)} /></Field>
                <Field label="Cc"><input className="input w-full" value={cc} onChange={e => setCc(e.target.value)} placeholder="Optional" /></Field>
              </div>
            )}
            <textarea
              className="input w-full min-h-[140px]"
              placeholder={mode === 'reply' ? 'Write your reply… (Ctrl+Enter to send)' : 'Write a note for your team… (Ctrl+Enter to save)'}
              value={body}
              onChange={e => setBody(e.target.value)}
              onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submit(false); }}
            />
            <div className="flex items-center gap-2 flex-wrap">
              {mode === 'reply' ? (
                <>
                  <button type="button" className="btn btn-primary inline-flex items-center gap-1.5" disabled={!canSubmit} onClick={() => submit(false)}>
                    <Send className="w-4 h-4" /> {replyMutation.isPending ? 'Sending…' : 'Send reply'}
                  </button>
                  <button type="button" className="btn btn-secondary inline-flex items-center gap-1.5" disabled={!canSubmit} onClick={() => submit(true)} title="Send and close — reopens automatically if they write back">
                    <Check className="w-4 h-4" /> Send &amp; close
                  </button>
                  <span className="text-xs text-muted ml-auto">
                    {c.mailbox ? <>Sends from <b>{c.mailbox.address}</b>. Signature added. Their reply lands here.</> : 'Sends from your first active CRM mailbox.'}
                  </span>
                </>
              ) : (
                <button type="button" className="btn btn-primary inline-flex items-center gap-1.5" disabled={!canSubmit} onClick={() => submit(false)}>
                  <StickyNote className="w-4 h-4" /> {noteMutation.isPending ? 'Saving…' : 'Add note'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div className="space-y-4">
          <AiBrief kind="conversation" id={c.id} label="Summarize this thread" />

          <SideCard title="Owner" icon={<User className="w-4 h-4" />}
            action={c.ownerId !== myId && <button type="button" className="text-xs text-primary hover:underline" onClick={() => updateMutation.mutate({ ownerId: myId })}>Take it</button>}
          >
            <select className="select w-full" value={c.ownerId ?? ''} onChange={e => updateMutation.mutate({ ownerId: e.target.value || null })} title="Who is responsible for answering. They get an email when handed a conversation.">
              <option value="">Unassigned</option>
              {staff.map((m: any) => { const uid = m.user?.id ?? m.userId; return <option key={uid} value={uid}>{m.user?.name || m.user?.email}</option>; })}
            </select>
          </SideCard>

          <SideCard title="Contact" icon={<User className="w-4 h-4" />}
            action={c.contact
              ? <button type="button" className="text-xs text-muted hover:text-err" title="Unlink" onClick={() => updateMutation.mutate({ contactId: null })}><X className="w-3.5 h-3.5" /></button>
              : <button type="button" className="text-xs text-primary hover:underline" onClick={() => setLinkContactOpen(true)}>Link existing</button>}
          >
            {c.contact ? (
              <div className="text-sm space-y-0.5">
                <Link to={`/crm/contacts/${c.contact.id}`} className="font-medium text-fg hover:text-primary">{c.contact.firstName} {c.contact.lastName}</Link>
                {c.contact.jobTitle && <p className="text-xs text-muted">{c.contact.jobTitle}</p>}
                {c.contact.email && <p className="text-xs text-muted">{c.contact.email}</p>}
                {c.contact.phone && <p className="text-xs text-muted">{c.contact.phone}</p>}
                <p className="text-xs capitalize" style={{ color: 'var(--scpa-text-muted)' }}>Status: <b className="text-fg">{c.contact.status}</b></p>
              </div>
            ) : (
              <div className="text-sm space-y-2">
                <p className="text-muted">{senderLabel(c)} is not a contact yet.</p>
                <button type="button" className="btn btn-secondary inline-flex items-center gap-1.5 w-full justify-center" onClick={() => createContactMutation.mutate()} disabled={createContactMutation.isPending}>
                  <UserPlus className="w-4 h-4" /> Create contact from sender
                </button>
              </div>
            )}
          </SideCard>

          <SideCard title="Organization" icon={<Building2 className="w-4 h-4" />}
            action={c.organization
              ? <button type="button" className="text-xs text-muted hover:text-err" title="Unlink" onClick={() => updateMutation.mutate({ organizationId: null })}><X className="w-3.5 h-3.5" /></button>
              : <button type="button" className="text-xs text-primary hover:underline" onClick={() => { setOrgName(String(c.formData?.Company ?? '')); setLinkOrgOpen(true); }}>Link or create</button>}
          >
            {c.organization ? (
              <Link to={`/crm/organizations/${c.organization.id}`} className="text-sm font-medium text-fg hover:text-primary">{c.organization.name}</Link>
            ) : (
              <p className="text-sm text-muted">{c.formData?.Company ? <>They wrote <b className="text-fg">{String(c.formData.Company)}</b> on the form.</> : 'None linked.'}</p>
            )}
          </SideCard>

          <SideCard title="Deal" icon={<Briefcase className="w-4 h-4" />}
            action={c.deal
              ? <button type="button" className="text-xs text-muted hover:text-err" title="Unlink" onClick={() => updateMutation.mutate({ dealId: null })}><X className="w-3.5 h-3.5" /></button>
              : <button type="button" className="text-xs text-primary hover:underline" onClick={() => setDealOpen(true)}>Create deal</button>}
          >
            {c.deal ? (
              <div className="text-sm">
                <Link to={`/crm/deals/${c.deal.id}`} className="font-medium text-fg hover:text-primary">{c.deal.title}</Link>
                <p className="text-xs text-muted flex items-center gap-1.5">
                  {c.deal.stage && <><span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.deal.stage.color }} />{c.deal.stage.name}</>}
                  {c.deal.value != null && <span>· {formatMoney(c.deal.value, c.deal.currency)}</span>}
                  <span className="capitalize">· {c.deal.status}</span>
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted">Turn this into an opportunity when it looks real.</p>
            )}
          </SideCard>

          <SideCard title="Tags" icon={<Paperclip className="w-4 h-4" />}>
            <div className="flex flex-wrap gap-1.5">
              {(c.tags || []).map(t => (
                <span key={t} className="badge bg-primary-muted text-primary inline-flex items-center gap-1">
                  {t}<button type="button" onClick={() => updateMutation.mutate({ tags: c.tags.filter(x => x !== t) })} aria-label={`Remove ${t}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <input className="input w-full" placeholder="Add a tag and press Enter" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
          </SideCard>

          <SideCard title="Details" icon={<Mail className="w-4 h-4" />}>
            <dl className="text-xs space-y-1">
              {[
                ['Source', SOURCE_LABEL[c.source]],
                ['Mailbox', c.mailbox ? `${c.mailbox.name} (${c.mailbox.address})` : '—'],
                ['Started', new Date(c.createdAt).toLocaleString()],
                ['First response', c.firstResponseAt ? formatHours((new Date(c.firstResponseAt).getTime() - new Date(c.createdAt).getTime()) / 36e5) : (c.source === 'manual' ? 'n/a' : 'not yet')],
                ['Last from them', c.lastInboundAt ? relativeTime(c.lastInboundAt) : '—'],
                ['Last from us', c.lastOutboundAt ? relativeTime(c.lastOutboundAt) : '—'],
                ['Messages', String(c.messageCount)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3"><dt className="text-muted">{k}</dt><dd className="text-fg text-right">{v}</dd></div>
              ))}
            </dl>
          </SideCard>

          <button type="button" className="btn btn-secondary w-full inline-flex items-center justify-center gap-1.5" onClick={() => setShowActivityForm(true)} disabled={!c.contactId && !c.dealId && !c.organizationId} title={!c.contactId && !c.dealId && !c.organizationId ? 'Link a contact first' : 'Schedule a call, meeting or task'}>
            <CalendarPlus className="w-4 h-4" /> Schedule a follow-up
          </button>
        </div>
      </div>

      {/* ── Modals ── */}
      <Modal open={forwardOpen} onClose={() => setForwardOpen(false)} title={`Forward CRM-${c.number}`} busy={forwardMutation.isPending}
        footer={<>
          <button type="button" className="btn btn-ghost" onClick={() => setForwardOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary inline-flex items-center gap-1.5" disabled={!forwardTo.trim() || forwardMutation.isPending} onClick={() => forwardMutation.mutate()}><Forward className="w-4 h-4" /> Forward</button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted">Sends the whole email thread to someone. If they reply, it lands back in this conversation.</p>
          <Field label="Forward to" required><input className="input w-full" value={forwardTo} onChange={e => setForwardTo(e.target.value)} placeholder="coworker@yourcompany.com" autoFocus /></Field>
          <Field label="Message"><textarea className="input w-full min-h-[100px]" value={forwardNote} onChange={e => setForwardNote(e.target.value)} placeholder="Optional note on top" /></Field>
        </div>
      </Modal>

      <Modal open={linkContactOpen} onClose={() => setLinkContactOpen(false)} title="Link an existing contact">
        <div className="space-y-3">
          <p className="text-sm text-muted">Pick who this conversation is with. To add them as a new contact instead, use <b>Create contact from sender</b>.</p>
          <select className="select w-full" defaultValue="" onChange={e => { if (e.target.value) { updateMutation.mutate({ contactId: e.target.value }); setLinkContactOpen(false); } }}>
            <option value="">Choose a contact…</option>
            {(contactsPick?.contacts ?? []).map(ct => <option key={ct.id} value={ct.id}>{ct.firstName} {ct.lastName ?? ''}{ct.email ? ` — ${ct.email}` : ''}</option>)}
          </select>
        </div>
      </Modal>

      <Modal open={linkOrgOpen} onClose={() => setLinkOrgOpen(false)} title="Organization" busy={createOrgMutation.isPending}
        footer={<>
          <button type="button" className="btn btn-ghost" onClick={() => setLinkOrgOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={createOrgMutation.isPending} onClick={() => createOrgMutation.mutate()}>Create &amp; link</button>
        </>}
      >
        <div className="space-y-4">
          <Field label="Link existing">
            <select className="select w-full" defaultValue="" onChange={e => { if (e.target.value) { updateMutation.mutate({ organizationId: e.target.value }); setLinkOrgOpen(false); } }}>
              <option value="">Choose an organization…</option>
              {(orgsPick?.organizations ?? []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
          <div className="text-xs text-muted text-center">— or create a new one —</div>
          <Field label="Organization name" hint="Pre-filled from the form or the sender's email domain when we can.">
            <input className="input w-full" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder={c.fromEmail.split('@')[1]} />
          </Field>
        </div>
      </Modal>

      <Modal open={dealOpen} onClose={() => setDealOpen(false)} title="Create a deal from this conversation" busy={createDealMutation.isPending}
        footer={<>
          <button type="button" className="btn btn-ghost" onClick={() => setDealOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!dealForm.title.trim() || createDealMutation.isPending} onClick={() => createDealMutation.mutate()}>Create deal</button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted">The deal is linked to this conversation{c.contact ? ', its contact' : ''}{c.organization ? ' and organization' : ''}, and owned by {c.owner?.name ?? 'you'}.</p>
          <Field label="Deal title" required><input className="input w-full" value={dealForm.title} onChange={e => setDealForm(f => ({ ...f, title: e.target.value }))} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Stage">
              <select className="select w-full" value={dealForm.stageId} onChange={e => setDealForm(f => ({ ...f, stageId: e.target.value }))}>
                {(stages ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Value"><input className="input w-full" type="number" min="0" value={dealForm.value} onChange={e => setDealForm(f => ({ ...f, value: e.target.value }))} placeholder="Optional" /></Field>
          </div>
        </div>
      </Modal>

      {showActivityForm && (
        <CrmActivityForm
          defaults={{ contactId: c.contactId ?? undefined, dealId: c.dealId ?? undefined, organizationId: c.organizationId ?? undefined }}
          onClose={() => setShowActivityForm(false)}
          onSaved={() => { setShowActivityForm(false); toast.success('Follow-up scheduled'); }}
        />
      )}
    </div>
  );
}
